/**
 * Iter XX.71.3 — AnonymousIdentityClaimModal
 *
 * Shown ONCE to anonymous users who have completed
 * FREE_MEANINGFUL_CONVERSATIONS meaningful chats. Bridges them
 * to a claimed identity BEFORE any Plus offer is evaluated.
 *
 * "Keep your cricket journey" — invites the anonymous player to save
 * their conversations by claiming their identity (sign-in flow).
 *
 * Trigger contract: Chat.jsx detects `identity_claim_prompt.show === true`
 * on the /web/chat response and mounts this AFTER the assistant reply
 * has fully rendered. Never interrupts a message.
 *
 * Data-testids:
 *   identity-claim-modal
 *   identity-claim-modal-cta        (primary — Save my journey)
 *   identity-claim-modal-dismiss    (secondary — Not now)
 */
import React, { useState } from "react";
import api from "../lib/api";

export default function AnonymousIdentityClaimModal({
  sessionId,
  claim,   // { session_number, threshold, title, cta, dismiss }
  onSignIn,
  onDismiss,
  onClose,
}) {
  const [busy, setBusy] = useState(false);

  const handleClaim = () => {
    // Delegate to the parent (Chat.jsx) which knows how to route to
    // sign-in (Emergent Google Auth). This keeps the auth surface in
    // one place and lets the modal stay presentation-only.
    if (onSignIn) onSignIn();
    if (onClose) onClose();
  };

  const handleDismiss = async () => {
    setBusy(true);
    try {
      await api.post("/web/identity-claim/dismiss",
        { session_id: sessionId });
    } catch { /* swallow — server marks-once; UI closes regardless */ }
    setBusy(false);
    if (onDismiss) onDismiss();
    if (onClose) onClose();
  };

  return (
    <div
      data-testid="identity-claim-modal"
      role="dialog"
      aria-labelledby="identity-claim-title"
      className="fixed inset-x-0 bottom-0 z-40 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleDismiss}
        aria-hidden
      />
      <div className="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-slate-800 bg-slate-950 p-5 shadow-2xl">
        <div className="text-[11px] uppercase tracking-widest text-emerald-400">
          You&apos;ve had {claim?.session_number ?? claim?.threshold} real coaching conversations
        </div>
        <div
          id="identity-claim-title"
          className="mt-1 text-base font-semibold text-slate-50"
        >
          {claim?.title || "Keep your cricket journey"}
        </div>
        <div className="mt-2 text-[13px] text-slate-400 leading-relaxed">
          Right now, Gully won&apos;t remember you next time you open
          this browser. Save your journey and everything you&apos;ve
          shared — matches, patterns, video reviews — stays with you.
          Nothing changes about the chat you just had.
        </div>
        <ul className="mt-3 space-y-1.5 text-[12px] text-slate-400">
          <li>· All your conversations, kept</li>
          <li>· Videos and insights, together in one place</li>
          <li>· A coach that remembers you next time</li>
        </ul>
        <button
          type="button"
          data-testid="identity-claim-modal-cta"
          onClick={handleClaim}
          disabled={busy}
          className="mt-4 w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-medium text-white hover:bg-emerald-500 transition-colors disabled:opacity-60"
        >
          {claim?.cta || "Save my journey"}
        </button>
        <button
          type="button"
          data-testid="identity-claim-modal-dismiss"
          onClick={handleDismiss}
          disabled={busy}
          className="mt-2 w-full rounded-xl border border-slate-800 py-2.5 text-sm font-medium text-slate-300 hover:bg-slate-900 transition-colors"
        >
          {claim?.dismiss || "Not now"}
        </button>
      </div>
    </div>
  );
}
